import { useCallback } from 'react';
import { usePlatformAdmin } from '@/hooks/usePlatformAdmin';
import { useUserRole, UserRole } from './useUserRole';
import { EXPECTED_PERMISSIONS } from '@/utils/rbac-verification';

export type PermissionLevel = 'none' | 'view' | 'edit' | 'admin';

export function usePermissions(organizationId?: string) {
  const { data: platformAdmin } = usePlatformAdmin();
  const { data: userRole, isLoading } = useUserRole(organizationId);

  const hasPermission = (module: string, requiredLevel: PermissionLevel = 'view'): boolean => {
    // Platform admins: check their specific permissions first (e.g., 'can_proxy_users')
    if (platformAdmin?.isAdmin && platformAdmin.permissions) {
      // Check if this is a platform admin permission
      if (module in platformAdmin.permissions) {
        return platformAdmin.permissions[module] === true;
      }
    }

    // Regular org-based permission check for modules
    if (isLoading || !userRole) return false;

    const userPermissions = EXPECTED_PERMISSIONS[userRole];
    if (!userPermissions) return false;

    const userLevel = userPermissions[module];
    if (!userLevel) return false;

    // Permission hierarchy: admin > edit > view > none
    const hierarchy = ['none', 'view', 'edit', 'admin'];
    const userLevelIndex = hierarchy.indexOf(userLevel);
    const requiredLevelIndex = hierarchy.indexOf(requiredLevel);

    return userLevelIndex >= requiredLevelIndex;
  };

  const canAccessModule = useCallback((module: string): boolean => {
    // Platform admins bypass all permission checks
    if (platformAdmin?.isAdmin && platformAdmin.permissions.can_access_all_orgs) {
      return true;
    }
    
    return hasPermission(module, 'view');
  }, [platformAdmin, hasPermission]);

  return {
    userRole,
    isLoading,
    hasPermission,
    canAccessModule,
  };
}
